import { connectRealtimeChannel } from '@dotstorming/realtime';

import {
  useBoardInteractionStore,
  useBoardSelectionStore,
  useWallAiJobStore,
  useWallContentStore,
  useWallParticipantsStore,
  useWallStore,
  type CardId,
  type WallBoard,
  type WallCard,
  type WallConnection,
  type WallId,
  type WallParticipant,
  type WallShape,
  type WallGroup,
} from '@walls/store';
import { clearLockedElementSelection } from '@walls/store/lockedSelection';
import { wallStorage, wallsApi } from './wallsApi';

export type BoardSocketPackage = {
  action: string;
  topic?: WallId;
  [key: string]: unknown;
};

type BoardSocketPackageWithTopic = BoardSocketPackage & { topic: WallId };

const queuedPackages: BoardSocketPackage[] = [];

let socket: ReturnType<typeof connectRealtimeChannel<BoardSocketPackageWithTopic>> | null = null;
let socketBoardId: WallId | null = null;

export function connectBoardWebsocket(boardId: WallId) {
  if (typeof window === 'undefined') {
    return () => undefined;
  }

  if (socket && socketBoardId === boardId) {
    return disconnectBoardWebsocket;
  }

  disconnectBoardWebsocket();
  socketBoardId = boardId;
  socket = connectRealtimeChannel<BoardSocketPackageWithTopic>({
    kind: 'wall',
    id: boardId,
    getToken: async () => (await wallsApi.getRealtimeToken(boardId)).token,
    onReady: () => {
      publishBoardSocketPackage({ topic: boardId, action: 'broadcastYourself' });
    },
    onContinuityLost: () => {
      window.location.reload();
    },
    onMessage: (socketPackage) => {
      if (socketPackage.action) processBoardSocketPackage(socketPackage);
    },
  });

  return disconnectBoardWebsocket;
}

export function disconnectBoardWebsocket() {
  if (socket) {
    socket.close();
    socket = null;
  }

  socketBoardId = null;
  queuedPackages.length = 0;
}

export function publishBoardSocketPackage(socketPackage: BoardSocketPackageWithTopic) {
  const board = useWallStore.getState().board;

  if (!board || !canPublishSocketPackage(board)) {
    return;
  }

  if (!socket) {
    return;
  }

  socket.publish(socketPackage);
}

export function processBoardSocketPackage(
  socketPackage: BoardSocketPackage,
  disableRequeue = false
) {
  const boardId = useWallStore.getState().board?.id;

  if (!boardId || (socketPackage.topic && socketPackage.topic !== boardId)) {
    return;
  }

  if (!disableRequeue && shouldQueuePackage(socketPackage)) {
    queueSocketPackage(socketPackage);
    return;
  }

  switch (socketPackage.action) {
    case 'updateBoardTitle':
      patchBoardTitle(socketPackage.title);
      break;
    case 'broadcastYourself':
      registerCurrentParticipant(boardId);
      break;
    case 'registerPresent':
      setParticipantLive(socketPackage.participantId);
      break;
    case 'participantAdded':
      addParticipant(socketPackage.participant);
      break;
    case 'participantDeleted':
      deleteParticipant(boardId, socketPackage.participantId);
      break;
    case 'participantRemoved':
      setParticipantGone(socketPackage.participantId);
      break;
    case 'groupAdded':
    case 'groupUpdated':
      upsertGroup(socketPackage.group);
      break;
    case 'groupDeleted':
      deleteGroup(socketPackage.groupId);
      break;
    case 'topicCardAdded':
      addCardFromSocket(boardId, socketPackage.card, false);
      break;
    case 'topicCardAddedToBoard':
      addCardFromSocket(boardId, socketPackage.card, true);
      break;
    case 'topicClusterAddedToBoard':
      upsertCardFromSocket(boardId, socketPackage.cluster);
      break;
    case 'topicCardUpdated':
    case 'topicClusterUpdated':
      updateCardFromSocket(boardId, socketPackage.card ?? socketPackage.cluster);
      break;
    case 'boardCardDeleted':
      deleteCardFromSocket(socketPackage.card);
      break;
    case 'boardClusterDeleted':
      deleteClusterFromSocket(socketPackage.cluster);
      break;
    case 'updateCardPosition':
    case 'updateClusterPosition':
      updateCardPosition(socketPackage.position);
      break;
    case 'updateCardSize':
    case 'updateClusterSize':
      updateCardSize(socketPackage.size);
      break;
    case 'undoCardResize':
    case 'undoClusterResize':
      undoCardResize(socketPackage.size);
      break;
    case 'clusterCard':
      updateCardFromSocket(boardId, socketPackage.card);
      break;
    case 'unclusterCard':
      unclusterCard(socketPackage.cluster);
      break;
    case 'lockCard':
      patchSocketCard(socketPackage.card, { isLocked: true });
      break;
    case 'lockCluster':
      patchSocketCard(socketPackage.cluster, { isLocked: true, persistCluster: true });
      break;
    case 'unlockCard':
      patchSocketCard(socketPackage.card, { isLocked: false });
      break;
    case 'unlockCluster':
      patchSocketCard(socketPackage.cluster, { isLocked: false });
      break;
    case 'unlockAll':
      unlockAllCards();
      break;
    case 'unlockCards':
      unlockCards(false);
      break;
    case 'unlockClusters':
      unlockCards(true);
      break;
    case 'createClusterFromCard':
    case 'createClusterFromCluster':
      createClusterFromSocket(boardId, socketPackage.cluster);
      break;
    case 'disableDrawingTools':
      useWallStore.getState().patchBoard({ allowDrawingTools: false });
      break;
    case 'enableDrawingTools':
      useWallStore.getState().patchBoard({ allowDrawingTools: true });
      break;
    case 'disableCardCreation':
      useWallStore.getState().patchBoard({ allowCardCreation: false });
      break;
    case 'enableCardCreation':
      useWallStore.getState().patchBoard({ allowCardCreation: true });
      break;
    case 'disableAutoAlign':
      useWallStore.getState().patchBoard({ autoAlignCards: false });
      break;
    case 'enableAutoAlign':
      useWallStore.getState().patchBoard({ autoAlignCards: true });
      break;
    case 'disableClustering':
      useWallStore.getState().patchBoard({ allowClustering: false });
      break;
    case 'enableClustering':
      useWallStore.getState().patchBoard({ allowClustering: true });
      break;
    case 'disableCardResizing':
      useWallStore.getState().patchBoard({ allowCardResizing: false });
      break;
    case 'enableCardResizing':
      useWallStore.getState().patchBoard({ allowCardResizing: true });
      break;
    case 'disableCardLocking':
      useWallStore.getState().patchBoard({ allowCardLocking: false });
      break;
    case 'enableCardLocking':
      useWallStore.getState().patchBoard({ allowCardLocking: true });
      break;
    case 'disableCardUnlocking':
      useWallStore.getState().patchBoard({ allowCardUnlocking: false });
      clearLockedElementSelection();
      break;
    case 'enableCardUnlocking':
      useWallStore.getState().patchBoard({ allowCardUnlocking: true });
      break;
    case 'disableAiTools':
      useWallStore.getState().patchBoard({ allowAiTools: false });
      break;
    case 'enableAiTools':
      useWallStore.getState().patchBoard({ allowAiTools: true });
      break;
    case 'themeGroupingJobStatus':
      updateThemeGroupingJob(socketPackage.job);
      break;
    case 'themeGroupingApplied':
      applyThemeGroupingSnapshot(boardId, socketPackage.cards, socketPackage.connections);
      break;
    case 'disableCardOwnerNames':
      useWallStore.getState().patchBoard({ showCardOwnerNames: false });
      break;
    case 'enableCardOwnerNames':
      useWallStore.getState().patchBoard({ showCardOwnerNames: true });
      break;
    case 'sortCluster':
      sortCluster(socketPackage.sortOrder);
      break;
    case 'addCardToCluster':
      addCardToCluster(socketPackage.updateDetail);
      break;
    case 'addClusterToCluster':
      addClusterToCluster(socketPackage.updateDetail);
      break;
    case 'removeCardFromCluster':
      removeCardFromCluster(socketPackage.updateDetail);
      break;
    case 'removeClusterFromCluster':
      removeClusterFromCluster(socketPackage.updateDetail);
      break;
    case 'collapseCluster':
      patchSocketCard(socketPackage.cluster, { collapsed: true });
      break;
    case 'expandCluster':
      patchSocketCard(socketPackage.cluster, { collapsed: false });
      break;
    case 'cardColorsChanged':
      updateCardColors(socketPackage.cardIds, socketPackage.color);
      break;
    case 'connectionInserted':
      upsertConnection(socketPackage.connection);
      break;
    case 'connectionColorUpdated':
      updateConnectionColor(socketPackage.connectionId, socketPackage.color);
      break;
    case 'connectionDeleted':
      deleteConnection(socketPackage.connection);
      break;
    case 'addShape':
      upsertShape(socketPackage.shape);
      break;
    case 'updateShape':
      updateShape(socketPackage.shape);
      break;
    case 'setShapeLocked':
      setShapeLocked(socketPackage.shapeId, socketPackage.isLocked);
      break;
    case 'deleteShape':
      deleteShape(socketPackage.shapeId);
      break;
  }
}

export function processBoardSocketQueue() {
  while (queuedPackages.length > 0) {
    const nextPackage = queuedPackages.shift();
    if (nextPackage) {
      processBoardSocketPackage(nextPackage, true);
    }
  }
}

useBoardInteractionStore.subscribe((state, previousState) => {
  const hadActiveInteraction =
    previousState.draggingIds.length > 0 || previousState.resizingIds.length > 0;
  const hasActiveInteraction = state.draggingIds.length > 0 || state.resizingIds.length > 0;

  if (hadActiveInteraction && !hasActiveInteraction) {
    processBoardSocketQueue();
  }
});

function canPublishSocketPackage(board: WallBoard) {
  return !board.isPrivate || Boolean(board.isOwner || board.isMember);
}

function shouldQueuePackage(socketPackage: BoardSocketPackage) {
  return getPackageElementIds(socketPackage).some((elementId) =>
    useBoardInteractionStore.getState().isInteractingWith(elementId)
  );
}

function queueSocketPackage(socketPackage: BoardSocketPackage) {
  const key = getQueueKey(socketPackage);
  const existingIndex = key
    ? queuedPackages.findIndex((candidate) => getQueueKey(candidate) === key)
    : -1;

  if (existingIndex >= 0) {
    queuedPackages.splice(existingIndex, 1, socketPackage);
    return;
  }

  queuedPackages.push(socketPackage);
}

function getQueueKey(socketPackage: BoardSocketPackage) {
  const [elementId] = getPackageElementIds(socketPackage);
  return elementId ? `${socketPackage.action}:${elementId}` : null;
}

function getPackageElementIds(socketPackage: BoardSocketPackage): string[] {
  switch (socketPackage.action) {
    case 'topicCardUpdated':
    case 'boardCardDeleted':
    case 'clusterCard':
    case 'lockCard':
    case 'unlockCard':
      return getSocketCardId(socketPackage.card);
    case 'topicClusterUpdated':
    case 'boardClusterDeleted':
    case 'unclusterCard':
    case 'lockCluster':
    case 'unlockCluster':
    case 'collapseCluster':
    case 'expandCluster':
      return getSocketCardId(socketPackage.cluster);
    case 'updateCardPosition':
    case 'updateClusterPosition':
      return getIdFromPayload(socketPackage.position);
    case 'updateCardSize':
    case 'updateClusterSize':
    case 'undoCardResize':
    case 'undoClusterResize':
      return getIdFromPayload(socketPackage.size);
    case 'addCardToCluster':
      return getUpdateDetailIds(socketPackage.updateDetail, ['cardId', 'clusterId']);
    case 'addClusterToCluster':
      return getUpdateDetailIds(socketPackage.updateDetail, ['sourceClusterId', 'targetClusterId']);
    case 'removeCardFromCluster':
      return getUpdateDetailIds(socketPackage.updateDetail, ['cardId']);
    case 'removeClusterFromCluster':
      return getUpdateDetailIds(socketPackage.updateDetail, ['clusterId']);
    case 'updateShape':
    case 'addShape':
      return getShapeId(socketPackage.shape);
    case 'setShapeLocked':
    case 'deleteShape':
      return typeof socketPackage.shapeId === 'string' ? [socketPackage.shapeId] : [];
    default:
      return [];
  }
}

function patchBoardTitle(title: unknown) {
  if (typeof title === 'string' && title.trim()) {
    useWallStore.getState().patchBoard({ title });
  }
}

function registerCurrentParticipant(boardId: WallId) {
  const participantId = wallStorage.getTopicUser(boardId);

  if (participantId) {
    publishBoardSocketPackage({
      action: 'registerPresent',
      topic: boardId,
      participantId,
    });
  }
}

function setParticipantLive(participantId: unknown) {
  if (typeof participantId === 'string') {
    useWallParticipantsStore.getState().setParticipantLive(participantId);
  }
}

function setParticipantGone(participantId: unknown) {
  if (typeof participantId === 'string') {
    useWallParticipantsStore.getState().setParticipantGone(participantId);
  }
}

function addParticipant(participant: unknown) {
  if (isWallParticipant(participant)) {
    useWallParticipantsStore.getState().addParticipant(participant);
  }
}

function upsertGroup(group: unknown) {
  if (isWallGroup(group)) {
    useWallParticipantsStore.getState().upsertGroup(group);
  }
}

function deleteGroup(groupId: unknown) {
  if (typeof groupId === 'string') {
    useWallParticipantsStore.getState().removeGroup(groupId);
  }
}

function deleteParticipant(boardId: WallId, participantId: unknown) {
  if (typeof participantId !== 'string') {
    return;
  }

  if (wallStorage.getTopicUser(boardId) === participantId) {
    wallStorage.clearTopicUser(boardId);
    window.location.reload();
    return;
  }

  useWallParticipantsStore.getState().removeParticipant(participantId);
  useWallContentStore.getState().removeCardsByParticipant(participantId);
  useWallContentStore.getState().removeConnectionsByParticipant(participantId);
}

function addCardFromSocket(boardId: WallId, card: unknown, placeOnBoard: boolean) {
  if (!isWallCardForBoard(boardId, card)) {
    return;
  }

  const contentStore = useWallContentStore.getState();
  contentStore.upsertCard(card);

  if (placeOnBoard && card.parentId) {
    contentStore.moveCardToCluster(card.id, card.parentId, card.zPos ?? 1);
  }
}

function upsertCardFromSocket(boardId: WallId, card: unknown) {
  if (isWallCardForBoard(boardId, card)) {
    useWallContentStore.getState().upsertCard(card);
  }
}

function updateCardFromSocket(boardId: WallId, card: unknown) {
  if (!isWallCardForBoard(boardId, card)) {
    return;
  }

  useWallContentStore.getState().updateCard(card.id, card);
}

function deleteCardFromSocket(card: unknown) {
  const cardId = isObject(card) && typeof card.id === 'string' ? card.id : null;

  if (cardId) {
    useWallContentStore.getState().removeCard(cardId);
    useWallContentStore.getState().removeConnectionsByCardId(cardId);
    useBoardSelectionStore.getState().removeSelectedId(cardId);
  }
}

function deleteClusterFromSocket(cluster: unknown) {
  const clusterId = isObject(cluster) && typeof cluster.id === 'string' ? cluster.id : null;

  if (clusterId) {
    useWallContentStore.getState().deleteClusterAndPromoteChildren(clusterId);
    useWallContentStore.getState().removeConnectionsByCardId(clusterId);
    useBoardSelectionStore.getState().removeSelectedId(clusterId);
  }
}

function updateCardPosition(position: unknown) {
  if (!isObject(position) || typeof position.id !== 'string') {
    return;
  }

  const xPos = getNumber(position.xPos);
  const yPos = getNumber(position.yPos);

  if (xPos != null && yPos != null) {
    useWallContentStore.getState().updateCard(position.id, { xPos, yPos });
  }
}

function updateCardSize(size: unknown) {
  if (!isObject(size) || typeof size.id !== 'string') {
    return;
  }

  const width = getNumber(size.width);
  const height = getNumber(size.height);

  useWallContentStore.getState().updateCard(size.id, {
    height: height ?? undefined,
    width: width ?? undefined,
  });
}

function undoCardResize(size: unknown) {
  if (isObject(size) && typeof size.id === 'string') {
    useWallContentStore.getState().updateCard(size.id, { height: undefined, width: undefined });
  }
}

function unclusterCard(cluster: unknown) {
  if (isObject(cluster) && typeof cluster.id === 'string') {
    useWallContentStore.getState().deleteClusterAndPromoteChildren(cluster.id);
  }
}

function patchSocketCard(card: unknown, patch: Partial<WallCard>) {
  if (isObject(card) && typeof card.id === 'string') {
    useWallContentStore.getState().updateCard(card.id, patch);
  }
}

function unlockAllCards() {
  const { cards, updateCards } = useWallContentStore.getState();
  updateCards(flattenCardIds(cards), { isLocked: false });
}

function unlockCards(clustersOnly: boolean) {
  const { cards, updateCards } = useWallContentStore.getState();
  updateCards(
    flattenCards(cards)
      .filter((card) => isClusterCard(card) === clustersOnly)
      .map((card) => card.id),
    {
      isLocked: false,
    }
  );
}

function createClusterFromSocket(boardId: WallId, cluster: unknown) {
  if (!isWallCardForBoard(boardId, cluster)) {
    return;
  }

  const sourceCardId = cluster.cards[0]?.id;
  if (sourceCardId) {
    useWallContentStore.getState().createClusterFromCards(sourceCardId, cluster.id);
  }

  useWallContentStore.getState().updateCard(cluster.id, cluster);
}

function sortCluster(sortOrder: unknown) {
  if (
    !isObject(sortOrder) ||
    typeof sortOrder.clusterId !== 'string' ||
    !Array.isArray(sortOrder.cards)
  ) {
    return;
  }

  const orderedIds = sortOrder.cards
    .map((card) => (isObject(card) && typeof card.id === 'string' ? card.id : null))
    .filter((cardId): cardId is string => Boolean(cardId));

  useWallContentStore.getState().sortClusterChildren(sortOrder.clusterId, orderedIds);
}

function addCardToCluster(updateDetail: unknown) {
  if (!isObject(updateDetail)) {
    return;
  }

  const cardId = typeof updateDetail.cardId === 'string' ? updateDetail.cardId : null;
  const clusterId = typeof updateDetail.clusterId === 'string' ? updateDetail.clusterId : null;
  const zPos = getNumber(updateDetail.zPos) ?? 1;

  if (cardId && clusterId) {
    useWallContentStore.getState().moveCardToCluster(cardId, clusterId, zPos);
    useWallContentStore.getState().removeConnectionsByCardId(cardId);
  }
}

function addClusterToCluster(updateDetail: unknown) {
  if (!isObject(updateDetail)) {
    return;
  }

  const sourceClusterId =
    typeof updateDetail.sourceClusterId === 'string' ? updateDetail.sourceClusterId : null;
  const targetClusterId =
    typeof updateDetail.targetClusterId === 'string' ? updateDetail.targetClusterId : null;
  const zPos = getNumber(updateDetail.zPos) ?? 1;

  if (sourceClusterId && targetClusterId) {
    useWallContentStore.getState().moveCardToCluster(sourceClusterId, targetClusterId, zPos);
    useWallContentStore.getState().removeConnectionsByCardId(sourceClusterId);
  }
}

function removeCardFromCluster(updateDetail: unknown) {
  detachFromCluster(updateDetail, 'cardId');
}

function removeClusterFromCluster(updateDetail: unknown) {
  detachFromCluster(updateDetail, 'clusterId');
}

function detachFromCluster(updateDetail: unknown, idKey: 'cardId' | 'clusterId') {
  if (!isObject(updateDetail) || typeof updateDetail[idKey] !== 'string') {
    return;
  }

  const cardId = updateDetail[idKey] as string;

  useWallContentStore
    .getState()
    .detachCardFromCluster(
      cardId,
      getNumber(updateDetail.xPos) ?? 0,
      getNumber(updateDetail.yPos) ?? 0
    );
}

function updateCardColors(cardIds: unknown, color: unknown) {
  if (!Array.isArray(cardIds) || typeof color !== 'string') {
    return;
  }

  useWallContentStore.getState().updateCards(
    cardIds.filter((cardId): cardId is string => typeof cardId === 'string'),
    { color }
  );
}

function upsertConnection(connection: unknown) {
  if (isWallConnection(connection)) {
    useWallContentStore.getState().upsertConnection(connection);
  }
}

function updateConnectionColor(connectionId: unknown, color: unknown) {
  if (typeof connectionId === 'string' && typeof color === 'string') {
    useWallContentStore.getState().updateConnection(connectionId, { color });
  }
}

function deleteConnection(connection: unknown) {
  if (isObject(connection) && typeof connection.id === 'string') {
    useWallContentStore.getState().removeConnection(connection.id);
    useBoardSelectionStore.getState().removeSelectedId(connection.id);
  }
}

function upsertShape(shape: unknown) {
  if (isWallShape(shape)) {
    useWallContentStore.getState().upsertShape(shape);
  }
}

function updateShape(shape: unknown) {
  if (isWallShape(shape)) {
    useWallContentStore.getState().updateShape(shape.id, shape);
  }
}

function setShapeLocked(shapeId: unknown, isLocked: unknown) {
  if (typeof shapeId === 'string' && typeof isLocked === 'boolean') {
    useWallContentStore.getState().updateShape(shapeId, { isLocked });
  }
}

function deleteShape(shapeId: unknown) {
  if (typeof shapeId === 'string') {
    useWallContentStore.getState().removeShape(shapeId);
    useBoardSelectionStore.getState().removeSelectedId(shapeId);
  }
}

function updateThemeGroupingJob(value: unknown) {
  const board = useWallStore.getState().board;

  if (
    !board?.isOwner ||
    !board.ownerId ||
    !isObject(value) ||
    typeof value.id !== 'string' ||
    typeof value.requestedBy !== 'string' ||
    value.requestedBy !== board.ownerId ||
    !isThemeGroupingJobStatus(value.status)
  ) {
    return;
  }

  useWallAiJobStore.getState().setThemeGroupingJob({
    id: value.id,
    requestedBy: value.requestedBy,
    status: value.status,
    error: typeof value.error === 'string' ? value.error : null,
  });
}

function applyThemeGroupingSnapshot(
  boardId: WallId,
  cards: unknown,
  connections: unknown
) {
  if (
    !Array.isArray(cards) ||
    !cards.every((card) => isWallCardTreeForBoard(boardId, card)) ||
    !Array.isArray(connections) ||
    !connections.every(isWallConnection)
  ) {
    return;
  }

  useWallContentStore.getState().replaceCardsAndConnections(cards, connections);
}

function isThemeGroupingJobStatus(
  value: unknown
): value is 'queued' | 'processing' | 'applying' | 'completed' | 'failed' {
  return (
    value === 'queued' ||
    value === 'processing' ||
    value === 'applying' ||
    value === 'completed' ||
    value === 'failed'
  );
}

function flattenCardIds(cards: WallCard[]): CardId[] {
  return flattenCards(cards).map((card) => card.id);
}

function flattenCards(cards: WallCard[]): WallCard[] {
  return cards.flatMap((card) => [card, ...flattenCards(card.cards)]);
}

function isClusterCard(card: WallCard) {
  return card.persistCluster || card.cards.length > 0 || card.shape === 'cluster';
}

function isWallCardForBoard(
  boardId: WallId,
  value: unknown
): value is WallCard & { boardId?: string } {
  return (
    isObject(value) &&
    typeof value.id === 'string' &&
    (value.boardId == null || value.boardId === boardId)
  );
}

function isWallCardTreeForBoard(boardId: WallId, value: unknown): value is WallCard {
  return (
    isWallCardForBoard(boardId, value) &&
    Array.isArray(value.cards) &&
    value.cards.every((card) => isWallCardTreeForBoard(boardId, card))
  );
}

function isWallParticipant(value: unknown): value is WallParticipant {
  return isObject(value) && typeof value.id === 'string' && typeof value.name === 'string';
}

function isWallGroup(value: unknown): value is WallGroup {
  return (
    isObject(value) &&
    typeof value.id === 'string' &&
    typeof value.name === 'string' &&
    Array.isArray(value.participantIds) &&
    value.participantIds.every((participantId) => typeof participantId === 'string')
  );
}

function isWallConnection(value: unknown): value is WallConnection {
  return isObject(value) && typeof value.id === 'string';
}

function isWallShape(value: unknown): value is WallShape {
  return isObject(value) && typeof value.id === 'string' && typeof value.type === 'string';
}

function isObject(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function getNumber(value: unknown) {
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}

function getSocketCardId(value: unknown) {
  return isObject(value) && typeof value.id === 'string' ? [value.id] : [];
}

function getShapeId(value: unknown) {
  return isObject(value) && typeof value.id === 'string' ? [value.id] : [];
}

function getIdFromPayload(value: unknown) {
  return isObject(value) && typeof value.id === 'string' ? [value.id] : [];
}

function getUpdateDetailIds(value: unknown, keys: string[]) {
  if (!isObject(value)) {
    return [];
  }

  return keys
    .map((key) => value[key])
    .filter((elementId): elementId is string => typeof elementId === 'string');
}
